/**
 * Términos y Condiciones del servicio — Grooming ERP.
 *
 * Se venden suscripciones y hasta hoy no había contrato publicado: lo único
 * que existía era la política de datos, que cubre otra cosa. Esto es lo que
 * regula la relación comercial.
 *
 * Está redactado a partir de lo que el sistema HACE de verdad, no de un
 * modelo genérico: el corte del acceso sin período de gracia, los medios de
 * pago que están implementados, la conservación de cinco años que impone la
 * la conservación de los soportes contables y el reparto de responsabilidades
 * sobre el cuidado de la mascota, que es del salón y no del software.
 *
 * Las tres cláusulas que son decisión comercial y no técnica —devoluciones
 * (sección 5), aviso de cambios de precio (sección 11) y límite de
 * responsabilidad (sección 10)— quedaron REVISADAS Y APROBADAS el 26/08/2026.
 * No hace falta volver a preguntarlas; si algún día cambian, sube la versión.
 *
 * Al cambiar el contenido hay que subir TERMINOS_VERSION y la fecha: la
 * versión anterior sigue siendo la que aceptó quien contrató antes.
 */

import { RESPONSABLE } from './politicaDatos';

export const TERMINOS_VERSION = '1.0';
export const TERMINOS_ACTUALIZADA = '2026-09-18';

export const TERMINOS_SECCIONES: { id: string; titulo: string; cuerpo: string }[] = [
  {
    id: 'objeto',
    titulo: '1. Objeto y aceptación',
    cuerpo: `Estos Términos y Condiciones regulan el acceso y uso de **${RESPONSABLE.productoComercial}**, un software en la nube de punto de venta, administración y facturación electrónica, prestado por **${RESPONSABLE.nombre}** (${RESPONSABLE.representante}), con NIT ${RESPONSABLE.nit} y domicilio en ${RESPONSABLE.direccion}, ${RESPONSABLE.ciudad}.

En estos términos, «el comerciante» es el establecimiento que contrata el servicio: la peluquería canina, el hotel o la guardería de mascotas.

Las cuentas de usuario las crea el administrador del sistema a solicitud del comerciante; no existe registro abierto al público. Al recibir sus credenciales, iniciar sesión o pagar una suscripción, el comerciante acepta estos términos en su totalidad. Si no está de acuerdo, debe abstenerse de usar el servicio.

El servicio se presta bajo la modalidad de **software como servicio (SaaS)**: no se vende ni se entrega el programa, se concede el derecho a usarlo mientras la suscripción esté vigente.`,
  },
  {
    id: 'cuenta',
    titulo: '2. La cuenta y sus usuarios',
    cuerpo: `El comerciante es responsable de:

- Mantener la confidencialidad de las credenciales de todos sus usuarios.
- Las operaciones que se realicen con sus usuarios, incluidas las facturas emitidas.
- Informar de inmediato cualquier uso no autorizado de su cuenta.

Las claves iniciales que entrega ${RESPONSABLE.nombre} son provisionales y deben cambiarse en el primer ingreso. ${RESPONSABLE.nombre} no conserva las claves de los usuarios: se almacenan cifradas y no son recuperables, solo restablecibles.

Cada usuario tiene un rol que determina a qué módulos accede. El comerciante decide qué rol tiene cada persona de su equipo y asume las consecuencias de esa asignación.`,
  },
  {
    id: 'suscripcion',
    titulo: '3. Suscripción, precios y pago',
    cuerpo: `El servicio se contrata por suscripción, según el plan acordado. El plan define el precio, la duración y los límites de uso.

**Medios de pago aceptados:**

- **Tarjeta a través de MercadoPago:** el acceso se restablece automáticamente cuando la pasarela aprueba el pago.
- **Nequi o transferencia bancaria:** el comerciante sube el comprobante y el acceso se restablece cuando ${RESPONSABLE.nombre} verifica el pago, dentro del siguiente día hábil.

Los precios están expresados en pesos colombianos. Cuando aplique IVA u otro impuesto, se informará en la factura correspondiente.

Los descuentos negociados aplican únicamente al período pactado y no se entienden extendidos a renovaciones posteriores, salvo acuerdo expreso.`,
  },
  {
    id: 'vigencia',
    titulo: '4. Vencimiento y suspensión del acceso',
    cuerpo: `**El acceso se suspende el día siguiente al vencimiento de la suscripción, sin período de gracia.** Esto se advierte en el sistema con anticipación: el comerciante recibe avisos antes de la fecha de vencimiento y ve la fecha exacta hasta la cual puede pagar.

La suspensión **no borra la información**. Los datos permanecen almacenados y vuelven a estar disponibles cuando se restablece el pago.

${RESPONSABLE.nombre} puede suspender el acceso, además, cuando:

- Se use el servicio para actividades ilícitas o contrarias a estos términos.
- Se intente vulnerar la seguridad del sistema o acceder a datos de otro comerciante.
- Lo ordene una autoridad competente.`,
  },
  {
    id: 'cancelacion',
    titulo: '5. Cancelación y devoluciones',
    cuerpo: `El comerciante puede cancelar su suscripción en cualquier momento comunicándolo a ${RESPONSABLE.emailContacto}. La cancelación surte efecto al final del período ya pagado; el servicio continúa disponible hasta esa fecha.

**Los períodos ya pagados no son reembolsables**, salvo que el servicio haya estado indisponible por causa atribuible a ${RESPONSABLE.nombre} durante un tiempo significativo del período facturado, caso en el cual se compensará con tiempo adicional de servicio.

Antes de cancelar, el comerciante debe descargar la información que necesite conservar. Ver la sección 8.`,
  },
  {
    id: 'servicio-mascotas',
    titulo: '6. El cuidado de la mascota es del salón',
    cuerpo: `Esta sección conviene leerla con calma, porque reparte responsabilidades sobre el servicio que se le presta al animal.

**${RESPONSABLE.nombre} pone la herramienta.** Grooming ERP registra citas, servicios, controles de salud, estadías y cobros. Es un sistema de gestión: no presta el servicio de peluquería, hotel ni guardería, y no sustituye el criterio del profesional.

**El salón responde por el cuidado del animal.** Es decir, responde por:

- El estado de la mascota durante y después del servicio, y por las decisiones del estilista.
- La veracidad de lo que registra: controles de salud, fechas de vacunación, notas de comportamiento y tarifas.
- Contar con el consentimiento del dueño para el servicio y para el uso de las fotografías que cargue.
- La atención veterinaria cuando la mascota la requiera.

**Los recordatorios son una ayuda, no una garantía.** El sistema calcula cuándo vence una purga, una pastilla de garrapatas o una vacuna a partir de las fechas que el salón registra, y facilita el aviso al dueño. Si la fecha se digitó mal, si el aviso no se envía o si el dueño no lo atiende, la consecuencia no es imputable a ${RESPONSABLE.nombre}. El calendario sanitario de la mascota lo define su veterinario.

${RESPONSABLE.nombre} **no responde** por daños a la mascota, por reclamaciones de los dueños, ni por sanciones derivadas del incumplimiento de las obligaciones sanitarias o comerciales del salón.`,
  },
  {
    id: 'disponibilidad',
    titulo: '7. Disponibilidad y soporte',
    cuerpo: `${RESPONSABLE.nombre} hace sus mejores esfuerzos por mantener el servicio disponible de forma continua, pero **no garantiza una disponibilidad ininterrumpida**. Puede haber interrupciones por mantenimiento, actualizaciones, fallas de terceros proveedores de infraestructura o causas de fuerza mayor.

Los mantenimientos programados se avisan con anticipación y se procuran en horarios de baja operación.

El soporte se presta por los canales publicados en ${RESPONSABLE.emailContacto}, en días hábiles. El sistema informa las novedades de cada versión dentro de la aplicación.`,
  },
  {
    id: 'datos',
    titulo: '8. La información del comerciante',
    cuerpo: `**La información cargada al sistema es del comerciante.** ${RESPONSABLE.nombre} la trata únicamente para prestar el servicio y no la comercializa ni la cede a terceros con fines distintos.

El comerciante puede exportar su información desde los módulos de reportes mientras su cuenta esté activa. Si necesita una copia completa al terminar la relación, debe solicitarla antes de la terminación.

**Los soportes de ventas, compras y cierres de caja se conservan por el término que exige la ley comercial y tributaria**, aun si el comerciante solicita la supresión de sus datos. Esta conservación es una obligación legal y no depende de la voluntad de las partes.

El tratamiento de datos personales se rige por la [Política de Tratamiento de Datos Personales](/politica-tratamiento-datos), que hace parte integral de estos términos.`,
  },
  {
    id: 'propiedad',
    titulo: '9. Propiedad intelectual',
    cuerpo: `El software, su código, su diseño, su marca y su documentación son propiedad de ${RESPONSABLE.nombre}. La suscripción concede un derecho de uso limitado, no exclusivo e intransferible, mientras esté vigente.

Queda prohibido copiar, descompilar, realizar ingeniería inversa, revender o ceder el acceso al servicio a terceros sin autorización escrita.

Los logotipos y contenidos que el comerciante cargue siguen siendo suyos; autoriza a ${RESPONSABLE.nombre} a usarlos únicamente para mostrarlos dentro del sistema y en los documentos que emita.`,
  },
  {
    id: 'responsabilidad',
    titulo: '10. Límite de responsabilidad',
    cuerpo: `${RESPONSABLE.nombre} responde por los daños directos que le sean imputables, hasta un monto equivalente a lo que el comerciante haya pagado por el servicio en los últimos tres (3) meses.

No responde por lucro cesante, pérdida de oportunidades comerciales ni daños indirectos derivados del uso o de la imposibilidad de uso del servicio.

Nada en esta cláusula limita las responsabilidades que la ley colombiana declare indisponibles.`,
  },
  {
    id: 'cambios',
    titulo: '11. Cambios en el servicio y en estos términos',
    cuerpo: `${RESPONSABLE.nombre} puede modificar, agregar o retirar funciones del sistema para mejorarlo o para adecuarlo a cambios normativos. Cuando un cambio afecte de forma sustancial la manera de operar, se avisará dentro de la aplicación.

Estos términos pueden actualizarse. La versión vigente es siempre la publicada en esta página, con su número de versión y su fecha. **Los cambios sustanciales se avisarán con al menos treinta (30) días de anticipación**, y quien no esté de acuerdo puede cancelar antes de que entren a regir.

Los ajustes de precio para renovaciones se avisarán con la misma anticipación.`,
  },
  {
    id: 'ley',
    titulo: '12. Ley aplicable y solución de controversias',
    cuerpo: `Estos términos se rigen por las leyes de la República de Colombia.

Las partes procurarán resolver directamente cualquier diferencia. De no lograrlo dentro de los treinta (30) días siguientes, se someterán a los jueces competentes de Colombia.

Para cualquier asunto relacionado con estos términos, el canal es **${RESPONSABLE.emailContacto}**.`,
  },
];
